/**
 * EXIF-Daten vom Original in das verkleinerte Foto übernehmen.
 *
 * Ein Bild, das über ein `canvas` neu gezeichnet wird, besteht nur noch aus
 * Bildpunkten — Aufnahmezeit, Kamera und **Ort** sind weg. Für einen
 * Reisebericht ist gerade der Ort die Angabe, die man später sucht. Also wird
 * der EXIF-Block aus der Originaldatei herausgeschnitten und in das neue JPEG
 * gesetzt.
 *
 * Ohne Fremdbibliothek, weil auch das Frontend ohne Abhängigkeiten auskommen
 * soll, die keiner mehr pflegt. Gebraucht wird davon ohnehin nur ein kleiner
 * Teil: den APP1-Block finden, eine einzige Marke darin korrigieren, den Block
 * wieder einsetzen.
 *
 * Aufbau einer JPEG-Datei: `FFD8` (Anfang), dann Segmente aus `FF`, Kennung,
 * Länge (zwei Byte, die Länge zählt sich selbst mit) und Inhalt. Der
 * EXIF-Block ist das Segment `FFE1`, dessen Inhalt mit `Exif\0\0` beginnt.
 */

const SOI = 0xd8
const SOS = 0xda
const APP1 = 0xe1

export function isJpeg(bytes: Uint8Array): boolean {
  return bytes.length > 3 && bytes[0] === 0xff && bytes[1] === SOI
}

/** Sucht den EXIF-Block und gibt ihn samt Kennung und Länge zurück. */
export function findExif(jpeg: Uint8Array): Uint8Array | null {
  if (!isJpeg(jpeg)) return null

  let at = 2
  while (at + 3 < jpeg.length) {
    if (jpeg[at] !== 0xff) return null

    const marker = jpeg[at + 1]
    // Ab hier kommen die Bildpunkte; danach steht kein EXIF mehr.
    if (marker === SOS) return null

    const hoch = jpeg[at + 2]
    const niedrig = jpeg[at + 3]
    if (hoch === undefined || niedrig === undefined) return null

    const length = (hoch << 8) | niedrig
    if (length < 2) return null

    if (marker === APP1 && startsWithExif(jpeg, at + 4)) {
      return jpeg.subarray(at, at + 2 + length)
    }
    at += 2 + length
  }
  return null
}

function startsWithExif(bytes: Uint8Array, at: number): boolean {
  return (
    bytes[at] === 0x45 && // E
    bytes[at + 1] === 0x78 && // x
    bytes[at + 2] === 0x69 && // i
    bytes[at + 3] === 0x66 && // f
    bytes[at + 4] === 0x00 &&
    bytes[at + 5] === 0x00
  )
}

/**
 * Setzt die Ausrichtung auf „aufrecht".
 *
 * Das Bild wurde beim Verkleinern schon gedreht — `createImageBitmap` richtet
 * es nach der EXIF-Marke aus. Übernähme man diese Marke unverändert, drehte
 * der Betrachter ein zweites Mal, und das Foto läge quer. Alles andere,
 * besonders die GPS-Angaben, bleibt unangetastet.
 */
export function uprightExif(segment: Uint8Array): Uint8Array<ArrayBuffer> {
  const copy = new Uint8Array(segment)
  // 2 Byte Kennung + 2 Byte Länge + 6 Byte "Exif\0\0" = Anfang des TIFF-Kopfs.
  const tiff = 10
  if (copy.length < tiff + 8) return copy

  const little = copy[tiff] === 0x49 && copy[tiff + 1] === 0x49
  const view = new DataView(copy.buffer, copy.byteOffset, copy.byteLength)

  // Der TIFF-Kopf verweist auf das erste Verzeichnis (IFD0).
  const ifd0 = tiff + view.getUint32(tiff + 4, little)
  if (ifd0 + 2 > copy.length) return copy

  const entries = view.getUint16(ifd0, little)
  for (let index = 0; index < entries; index++) {
    const entry = ifd0 + 2 + index * 12
    if (entry + 12 > copy.length) break

    // 0x0112 ist die Ausrichtung. Sie steht als SHORT unmittelbar im Eintrag.
    if (view.getUint16(entry, little) === 0x0112) {
      view.setUint16(entry + 8, 1, little)
      break
    }
  }
  return copy
}

/**
 * Wann und wo aufgenommen wurde.
 *
 * `at` ist eine ISO-Zeit in UTC, `lat`/`lon` sind Grad. Alles darf fehlen: ein
 * Bildschirmfoto hat keinen Ort, ein weitergeschicktes Bild oft keine Zeit.
 */
export interface Aufnahme {
  at: string | null
  lat: number | null
  lon: number | null
  /**
   * Die Uhr, auf der die Kamera stand — Minuten zu UTC.
   *
   * Aus `OffsetTimeOriginal`, das jedes iPhone seit iOS 13 schreibt. Sie ist
   * die beste Auskunft darüber, welche Zeit an diesem Ort galt: das Telefon
   * war dort, der Browser, der den Bericht später öffnet, nicht.
   *
   * `null` heißt: das Bild sagt nichts dazu.
   */
  tz_offset_min: number | null
}

/** Die Marken, die hier gebraucht werden. */
const TAG = {
  DATETIME: 0x0132,
  EXIF_IFD: 0x8769,
  GPS_IFD: 0x8825,
  DATETIME_ORIGINAL: 0x9003,
  OFFSET_TIME_ORIGINAL: 0x9011,
  GPS_LAT_REF: 0x0001,
  GPS_LAT: 0x0002,
  GPS_LON_REF: 0x0003,
  GPS_LON: 0x0004,
  GPS_TIME: 0x0007,
  GPS_DATE: 0x001d,
} as const

/** Ein Eintrag im Verzeichnis: Typ, Anzahl, und wo der Wert liegt. */
interface Eintrag {
  typ: number
  anzahl: number
  /** Ab Anfang des Blocks, nicht ab TIFF-Kopf. */
  wertAb: number
}

/** Wie viele Byte ein Wert dieses Typs belegt. */
const BREITE: Record<number, number> = { 1: 1, 2: 1, 3: 2, 4: 4, 5: 8, 7: 1, 9: 4, 10: 8 }

/**
 * Aufnahmeort und -zeit aus einem JPEG lesen.
 *
 * Gelesen wird **vor** dem Verkleinern, aus der Originaldatei — danach sind
 * die Angaben zwar noch im Block, aber der Umweg wäre unnötig.
 *
 * **Diese Funktion wirft nie.** Was sie nicht sicher lesen kann, meldet sie als
 * `null`. Ein Foto, dessen EXIF-Block krumm ist, soll trotzdem hochgehen — die
 * Angaben sind eine Zugabe, das Bild ist die Sache.
 */
export function readAufnahme(jpeg: Uint8Array): Aufnahme {
  const leer: Aufnahme = { at: null, lat: null, lon: null, tz_offset_min: null }
  try {
    const block = findExif(jpeg)
    if (block === null) return leer

    // 2 Byte Kennung + 2 Byte Länge + 6 Byte "Exif\0\0" = Anfang des TIFF-Kopfs.
    const tiff = 10
    if (block.length < tiff + 8) return leer

    const little = block[tiff] === 0x49 && block[tiff + 1] === 0x49
    const view = new DataView(block.buffer, block.byteOffset, block.byteLength)

    const ifd0 = tiff + view.getUint32(tiff + 4, little)
    const haupt = verzeichnis(view, block.length, ifd0, tiff, little)

    const exif = zeiger(view, haupt, TAG.EXIF_IFD, little)
    const gps = zeiger(view, haupt, TAG.GPS_IFD, little)

    const exifTeil =
      exif === null ? new Map() : verzeichnis(view, block.length, tiff + exif, tiff, little)
    const gpsTeil =
      gps === null ? new Map() : verzeichnis(view, block.length, tiff + gps, tiff, little)

    return {
      at: zeitpunkt(view, haupt, exifTeil, gpsTeil, little),
      ...ort(view, gpsTeil, little),
      tz_offset_min: versatz(view, exifTeil),
    }
  } catch {
    // Ein kaputter Block ist kein Grund, ein Foto abzulehnen.
    return leer
  }
}

/**
 * Ein Verzeichnis einlesen.
 *
 * Jeder Eintrag ist zwölf Byte: Marke, Typ, Anzahl, Wert. Passt der Wert in
 * vier Byte, steht er unmittelbar im Eintrag; sonst steht dort ein Abstand zum
 * TIFF-Kopf. Beides wird hier auf eine Stelle im Block umgerechnet, damit die
 * Leser darunter nicht jedes Mal wieder unterscheiden müssen.
 */
function verzeichnis(
  view: DataView,
  laenge: number,
  ab: number,
  tiff: number,
  little: boolean,
): Map<number, Eintrag> {
  const eintraege = new Map<number, Eintrag>()
  if (ab + 2 > laenge) return eintraege

  const anzahl = view.getUint16(ab, little)
  for (let index = 0; index < anzahl; index++) {
    const eintrag = ab + 2 + index * 12
    if (eintrag + 12 > laenge) break

    const marke = view.getUint16(eintrag, little)
    const typ = view.getUint16(eintrag + 2, little)
    const menge = view.getUint32(eintrag + 4, little)
    const breite = BREITE[typ]
    if (breite === undefined) continue

    const bytes = breite * menge
    const wertAb = bytes <= 4 ? eintrag + 8 : tiff + view.getUint32(eintrag + 8, little)
    // Ein Abstand, der aus dem Block hinausführt, ist kaputt -- und wäre der
    // eine Fall, in dem eine Leseroutine hier über die Grenze liefe.
    if (wertAb < 0 || wertAb + bytes > laenge) continue

    eintraege.set(marke, { typ, anzahl: menge, wertAb })
  }
  return eintraege
}

/** Ein Verweis auf ein anderes Verzeichnis (LONG). */
function zeiger(
  view: DataView,
  woraus: Map<number, Eintrag>,
  marke: number,
  little: boolean,
): number | null {
  const eintrag = woraus.get(marke)
  if (eintrag === undefined || eintrag.typ !== 4) return null
  return view.getUint32(eintrag.wertAb, little)
}

/** Eine Zeichenkette, bis zur abschließenden Null. */
function text(view: DataView, woraus: Map<number, Eintrag>, marke: number): string {
  const eintrag = woraus.get(marke)
  if (eintrag === undefined || eintrag.typ !== 2) return ''

  let ergebnis = ''
  for (let index = 0; index < eintrag.anzahl; index++) {
    const zeichen = view.getUint8(eintrag.wertAb + index)
    if (zeichen === 0) break
    ergebnis += String.fromCharCode(zeichen)
  }
  return ergebnis.trim()
}

/** Ein Bruch — oder mehrere hintereinander, wie bei Grad/Minute/Sekunde. */
function brueche(
  view: DataView,
  woraus: Map<number, Eintrag>,
  marke: number,
  wieviele: number,
  little: boolean,
): number[] | null {
  const eintrag = woraus.get(marke)
  if (eintrag === undefined || eintrag.typ !== 5 || eintrag.anzahl < wieviele) return null

  const werte: number[] = []
  for (let index = 0; index < wieviele; index++) {
    const ab = eintrag.wertAb + index * 8
    const nenner = view.getUint32(ab + 4, little)
    if (nenner === 0) return null
    werte.push(view.getUint32(ab, little) / nenner)
  }
  return werte
}

/**
 * Der Aufnahmeort, in Grad.
 *
 * EXIF führt Breite und Länge als Grad, Minute, Sekunde — und die Himmels-
 * richtung getrennt daneben. Süd und West sind negativ.
 */
function ort(
  view: DataView,
  gps: Map<number, Eintrag>,
  little: boolean,
): { lat: number | null; lon: number | null } {
  const breite = brueche(view, gps, TAG.GPS_LAT, 3, little)
  const laenge = brueche(view, gps, TAG.GPS_LON, 3, little)
  if (breite === null || laenge === null) return { lat: null, lon: null }

  const lat = grad(breite) * (text(view, gps, TAG.GPS_LAT_REF).toUpperCase() === 'S' ? -1 : 1)
  const lon = grad(laenge) * (text(view, gps, TAG.GPS_LON_REF).toUpperCase() === 'W' ? -1 : 1)

  if (!Number.isFinite(lat) || !Number.isFinite(lon)) return { lat: null, lon: null }
  if (lat < -90 || lat > 90 || lon < -180 || lon > 180) return { lat: null, lon: null }
  // 0/0 liegt im Golf von Guinea und ist in der Praxis immer ein Empfänger
  // ohne Fix. Der Server wirft es ohnehin weg; hier steht es, damit die
  // Oberfläche daraus erst gar keinen Vorschlag baut.
  if (lat === 0 && lon === 0) return { lat: null, lon: null }

  return { lat: Number(lat.toFixed(6)), lon: Number(lon.toFixed(6)) }
}

function grad(werte: number[]): number {
  const [g = 0, m = 0, s = 0] = werte
  return g + m / 60 + s / 3600
}

/**
 * Der Aufnahmezeitpunkt — und die Reihenfolge ist der ganze Punkt.
 *
 * 1. **GPS-Datum und -Uhrzeit sind UTC**, ohne jede Annahme. Wo sie stehen,
 *    ist die Sache entschieden.
 * 2. `DateTimeOriginal` ist Ortszeit **ohne Zone**. Zusammen mit
 *    `OffsetTimeOriginal` (das jedes iPhone seit iOS 13 schreibt) ist sie
 *    trotzdem eindeutig.
 * 3. Bleibt nur die nackte Ortszeit, wird die Zone dieses Geräts angenommen.
 *    Das ist eine Vermutung — meistens die richtige, denn wer unterwegs Fotos
 *    schickt, hat die Uhr des Telefons dabei. Deshalb ist die Zeit im Formular
 *    immer überschreibbar.
 */
/**
 * Auf welcher Uhr die Kamera stand — `OffsetTimeOriginal` in Minuten.
 *
 * **Die beste Auskunft über die Zeit an einem Ort, die es gibt.** Das Telefon
 * war dort; der Browser, der den Bericht später öffnet, war es nicht. Steht
 * hier „+02:00", ist damit entschieden, was „14:30" an diesem Punkt bedeutet.
 *
 * Was nicht genau der Form entspricht, fällt weg: eine halb gelesene Zone wäre
 * schlechter als keine.
 */
function versatz(view: DataView, exif: Map<number, Eintrag>): number | null {
  const roh = text(view, exif, TAG.OFFSET_TIME_ORIGINAL)
  const passt = /^([+-])(\d{2}):(\d{2})$/.exec(roh)
  if (passt === null) return null

  const [, zeichen = '+', stunden = '0', minuten = '0'] = passt
  const summe = Number(stunden) * 60 + Number(minuten)
  if (!Number.isFinite(summe) || summe > 840) return null
  return zeichen === '-' ? -summe : summe
}

function zeitpunkt(
  view: DataView,
  haupt: Map<number, Eintrag>,
  exif: Map<number, Eintrag>,
  gps: Map<number, Eintrag>,
  little: boolean,
): string | null {
  const gpsDatum = text(view, gps, TAG.GPS_DATE)
  const gpsUhr = brueche(view, gps, TAG.GPS_TIME, 3, little)
  if (gpsDatum !== '' && gpsUhr !== null) {
    const teile = gpsDatum.split(':').map(Number)
    if (teile.length === 3 && teile.every(Number.isFinite)) {
      const [jahr = 0, monat = 1, tag = 1] = teile
      const [stunde = 0, minute = 0, sekunde = 0] = gpsUhr
      const ms = Date.UTC(
        jahr,
        monat - 1,
        tag,
        Math.floor(stunde),
        Math.floor(minute),
        Math.floor(sekunde),
      )
      const gelesen = plausibel(new Date(ms))
      if (gelesen !== null) return gelesen
    }
  }

  const roh =
    text(view, exif, TAG.DATETIME_ORIGINAL) || text(view, haupt, TAG.DATETIME)
  // "2023:08:01 09:14:22" -- Doppelpunkte auch im Datum, deshalb kein Date.parse.
  const passt = /^(\d{4}):(\d{2}):(\d{2})[ T](\d{2}):(\d{2}):(\d{2})/.exec(roh)
  if (passt === null) return null

  const [, jahr = '', monat = '', tag = '', stunde = '', minute = '', sekunde = ''] = passt
  const zone = text(view, exif, TAG.OFFSET_TIME_ORIGINAL)
  const mitZone = /^[+-]\d{2}:\d{2}$/.test(zone) ? zone : null

  const datum = mitZone
    ? new Date(`${jahr}-${monat}-${tag}T${stunde}:${minute}:${sekunde}${mitZone}`)
    : new Date(
        Number(jahr),
        Number(monat) - 1,
        Number(tag),
        Number(stunde),
        Number(minute),
        Number(sekunde),
      )

  return plausibel(datum)
}

/**
 * Was offensichtlich nicht stimmt, wird verworfen.
 *
 * Eine Kamera ohne Batterie fängt bei 1980 an, und ein Highlight, das im
 * letzten Jahrhundert liegt, sortiert die ganze Zeitleiste durcheinander. Ein
 * Tag Luft nach oben, damit eine Uhr, die ein paar Stunden vorgeht, nichts
 * kaputtmacht.
 */
function plausibel(datum: Date): string | null {
  const ms = datum.getTime()
  if (!Number.isFinite(ms)) return null
  if (datum.getUTCFullYear() < 1990) return null
  if (ms > Date.now() + 24 * 3600 * 1000) return null
  return datum.toISOString()
}

/**
 * Setzt einen EXIF-Block in ein JPEG ein.
 *
 * Der Block gehört unmittelbar hinter den Dateianfang. Ein bereits
 * vorhandener EXIF-Block im Ziel wird ersetzt — der Browser schreibt zwar
 * keinen, aber verlassen sollte man sich darauf nicht.
 */
export function withExif(jpeg: Uint8Array, exif: Uint8Array): Uint8Array<ArrayBuffer> {
  if (!isJpeg(jpeg)) return new Uint8Array(jpeg)

  const vorhanden = findExif(jpeg)
  const rest = vorhanden
    ? concat(
        jpeg.subarray(2, vorhanden.byteOffset - jpeg.byteOffset),
        jpeg.subarray(vorhanden.byteOffset - jpeg.byteOffset + vorhanden.length),
      )
    : jpeg.subarray(2)

  return concat(new Uint8Array([0xff, SOI]), exif, rest)
}

function concat(...teile: Uint8Array[]): Uint8Array<ArrayBuffer> {
  const gesamt = teile.reduce((summe, teil) => summe + teil.length, 0)
  const ergebnis = new Uint8Array(new ArrayBuffer(gesamt))
  let at = 0
  for (const teil of teile) {
    ergebnis.set(teil, at)
    at += teil.length
  }
  return ergebnis
}
